import { MdContactPage, MdEmail, MdFmdGood, MdPhone } from 'react-icons/md';
import { Address } from 'types';
import { CopyContent } from 'pages/profile/components';
import {
  getLocalStorageItem,
  removeSpecialCharacters,
  setLocalStorageItem,
} from 'helpers';
import { LocalStorageKeys, TEST_IDS } from 'commons';
import { HTMLAttributes, useState } from 'react';
import { FaGear } from 'react-icons/fa6';
import { ToggleButton } from 'components';

interface Props {
  address: Address;
  notificationEmail: string[];
  discountAllowed: number;
  exchangeOmniEnable: boolean | null;
  participateInPickUpStore: boolean;
  participateInStoreShipping: boolean;
}

const MISSING_INFORMATION = 'Não informado';

function IconWrapper({ children }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className="flex items-center justify-center w-3 h-3 [&_svg]:w-full">
      {children}
    </div>
  );
}

export function StoreInfos({
  address,
  notificationEmail,
  discountAllowed,
  exchangeOmniEnable,
  participateInPickUpStore,
  participateInStoreShipping,
}: Props) {
  const {
    streetName,
    streetNumber,
    complement,
    district,
    town,
    postalCode,
    region,
    phone1,
    phone2,
  } = address;

  const storedSoundNotification = getLocalStorageItem(
    LocalStorageKeys.SoundNotification,
    true
  );

  const [enableSoundNotification, setEnableSoundNotification] =
    useState<boolean>(storedSoundNotification);

  function changeSoundNotification(notificationChecked: boolean) {
    setEnableSoundNotification(notificationChecked);

    setLocalStorageItem(
      LocalStorageKeys.SoundNotification,
      notificationChecked
    );
  }

  function formatAnswer(answer: boolean): string {
    return answer ? 'Ativado' : 'Desativado';
  }

  function renderLabel(text: string) {
    return <p className="text-xs font-bold">{text}</p>;
  }

  function renderDescription(text: string) {
    const displayText = text || MISSING_INFORMATION;

    return <p className="text-xs">{displayText}</p>;
  }

  const renderEmails = notificationEmail.map((email, index) => {
    if (!email) return null;

    return (
      <CopyContent key={`${email}-${index}`} copyContent={email}>
        <IconWrapper>
          <MdEmail />
        </IconWrapper>

        <p className="text-xs">{email}</p>
      </CopyContent>
    );
  });

  const renderPhone = [phone1, phone2].map((phone) => {
    if (!phone) return null;

    return (
      <CopyContent copyContent={phone1 || phone2}>
        <IconWrapper>
          <MdPhone />
        </IconWrapper>

        <p className="text-xs">{phone}</p>
      </CopyContent>
    );
  });

  function renderAddress() {
    const formattedPostalCode = removeSpecialCharacters(postalCode);

    return (
      <div className="flex flex-col gap-2">
        <CopyContent copyContent={streetName}>
          {renderLabel('Logradouro')}

          {renderDescription(streetName)}
        </CopyContent>

        <CopyContent copyContent={streetNumber}>
          {renderLabel('Número')}

          {renderDescription(streetNumber)}
        </CopyContent>

        {complement && (
          <CopyContent copyContent={complement}>
            {renderLabel('Compl.')}

            {renderDescription(complement)}
          </CopyContent>
        )}

        <CopyContent copyContent={district}>
          {renderLabel('Bairro')}

          {renderDescription(district)}
        </CopyContent>

        <CopyContent copyContent={town}>
          {renderLabel('Cidade')}

          {renderDescription(town)}
        </CopyContent>

        <CopyContent copyContent={region.isoCode}>
          {renderLabel('UF')}

          {renderDescription(region.isoCode)}
        </CopyContent>

        <CopyContent copyContent={formattedPostalCode}>
          {renderLabel('CEP')}

          {renderDescription(postalCode)}
        </CopyContent>
      </div>
    );
  }

  function renderHorizontalLine() {
    return <div className="w-full h-px bg-neutral-300" />;
  }

  return (
    <div className="flex flex-col gap-6 flex-1 min-w-72">
      <div
        className="flex flex-col gap-2"
        data-testid={TEST_IDS.STORE_INFOS_DIV}
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-4 h-4 [&_svg]:w-full">
            <MdContactPage />
          </div>

          <h3 className="text-base font-bold">Contato</h3>
        </div>

        {renderHorizontalLine()}

        {renderEmails}

        {renderPhone}
      </div>

      <div
        className="flex flex-col gap-2"
        data-testid={TEST_IDS.STORE_ADDRESS_DIV}
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-4 h-4 [&_svg]:w-full">
            <MdFmdGood />
          </div>

          <h3 className="text-base font-bold">Endereço</h3>
        </div>

        {renderHorizontalLine()}

        {renderAddress()}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-4 h-4 [&_svg]:w-full">
            <FaGear />
          </div>

          <h3 className="text-base font-bold">Configuração Geral</h3>
        </div>

        {renderHorizontalLine()}

        <div className="flex items-center justify-between">
          {renderLabel('Notificação sonora')}

          <ToggleButton
            checked={enableSoundNotification}
            onChange={(e) => {
              const { checked } = e.target;

              changeSoundNotification(checked);
            }}
          />
        </div>

        <div className="flex items-center justify-between">
          {renderLabel('Retire na loja')}

          {renderDescription(formatAnswer(participateInPickUpStore))}
        </div>

        <div className="flex items-center justify-between">
          {renderLabel('Entrega pela loja')}

          {renderDescription(formatAnswer(participateInStoreShipping))}
        </div>

        <div className="flex items-center justify-between">
          {renderLabel('Trocas Omni')}

          {renderDescription(formatAnswer(exchangeOmniEnable || false))}
        </div>

        <div className="flex items-center justify-between">
          {renderLabel('Desconto global')}

          {renderDescription(`${discountAllowed}%`)}
        </div>
      </div>
    </div>
  );
}
